import { BrainCircuit, Check, ChevronRight, ExternalLink, Laptop, RefreshCw, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import type { DownloadOperatingSystem } from "@studycoach/release-manifest";
import { DownloadPanel } from "../components/DownloadPanel";
import { PageMeta } from "../components/PageMeta";
import {
  detectedOperatingSystem,
  releaseDate,
  releaseManifest,
  releaseNotesAnchor,
  stableReleaseSummary
} from "../data/release-info";

export function DownloadPage() {
  const detected = useMemo(() => detectedOperatingSystem(navigator.userAgent, navigator.platform), []);
  const [selected, setSelected] = useState<DownloadOperatingSystem>(detected ?? "macos");
  const selectedDownload = releaseManifest.downloads.find((item) => item.os === selected);
  const alternative = releaseManifest.downloads.find((item) => item.os !== selected);
  const currentRelease = stableReleaseSummary;

  return (
    <main className="page-main">
      <PageMeta title="下载" description="查看 StudyCoach 最新版本、macOS 与 Windows 安装状态、系统要求和 SHA-256 校验值。" />
      <section className="page-hero shell download-hero">
        <div>
          <span className="eyebrow">稳定通道 · {releaseDate(releaseManifest.pub_date)}</span>
          <h1>下载 StudyCoach</h1>
          <p>当前最新版本 <strong>v{releaseManifest.version}</strong>；安装状态、系统要求与安全提示都在下方如实列出。</p>
        </div>
        <div className="version-orbit"><span>Stable</span><strong>{releaseManifest.version}</strong><small>最新版本</small></div>
      </section>

      <section className="shell download-layout">
        <div className="platform-switcher" role="group" aria-label="选择操作系统">
          <button
            type="button"
            className={selected === "macos" ? "is-active" : ""}
            aria-pressed={selected === "macos"}
            aria-controls="download-panel"
            onClick={() => setSelected("macos")}
          >
            <span className="platform-symbol">⌘</span><span><strong>macOS</strong><small>Apple Silicon</small></span>
            {detected === "macos" ? <em>当前设备</em> : null}
          </button>
          <button
            type="button"
            className={selected === "windows" ? "is-active" : ""}
            aria-pressed={selected === "windows"}
            aria-controls="download-panel"
            onClick={() => setSelected("windows")}
          >
            <span className="platform-symbol">⊞</span><span><strong>Windows</strong><small>Windows 10 / 11 x64</small></span>
            {detected === "windows" ? <em>当前设备</em> : null}
          </button>
        </div>

        {selectedDownload ? <DownloadPanel download={selectedDownload} recommended={detected === selected} /> : null}

        <aside className="download-side">
          <article>
            <div className="side-icon"><RefreshCw size={19} /></div>
            <h3>安装一次，之后在软件内更新</h3>
            <p>StudyCoach 定期检查稳定通道。新版本出现时，会先展示版本号与更新说明，由你确认后下载、验签、安装并重启。</p>
          </article>
          <article>
            <div className="side-icon"><ShieldCheck size={19} /></div>
            <h3>更新包必须通过签名</h3>
            <p>版本号新不代表可信。客户端使用固定公钥验证更新包；签名不匹配时拒绝安装，当前版本继续可用。</p>
          </article>
          <article>
            <div className="side-icon"><BrainCircuit size={19} /></div>
            <h3>安装后可以接哪些 AI</h3>
            <p>开箱即用的托管 AI 之外，可以自带兼容 API key，也可以连接本机的 Codex 与 Grok Build 客户端；Claude 模型通过独立 Anthropic API key 使用，绝不读取 Claude.ai 订阅凭据。</p>
          </article>
          <article>
            <div className="side-icon"><Laptop size={19} /></div>
            <h3>换一个系统？</h3>
            <p>{alternative?.label ?? "其他平台"} 的状态也在本页上方切换查看。当前不会自动下载与你设备不匹配的安装器。</p>
          </article>
        </aside>
      </section>

      <section className="shell release-summary-card">
        <div>
          <span className="eyebrow">v{releaseManifest.version} 更新摘要</span>
          <h2>{currentRelease.label}</h2>
        </div>
        <p>{currentRelease.summary}</p>
        <Link className="text-link" to={releaseNotesAnchor()}>阅读完整版本说明 <ChevronRight size={16} /></Link>
      </section>

      <section className="shell integrity-note">
        <div><Check size={16} /><span><strong>一个稳定清单</strong>官网与客户端共同读取 <code>/updates/stable/latest.json</code>，版本、下载地址和签名不再各写一份。</span></div>
        <a href="/updates/stable/latest.json">查看机器可读清单 <ExternalLink size={14} /></a>
      </section>
    </main>
  );
}
